import {
  AREA_PRESETS,
  AREA_TYPES,
  RACK_KIND_LABELS,
  RACK_PRESETS,
  STORAGE_KINDS,
  aisleAt,
  aisleIndex,
  lowerLabel,
  suggestedBinLitres,
  type AisleUse,
  type AreaBlockInput,
  type FloorBuilding,
  type AreaType,
  type RackGeometry,
  type RackKind,
  type StorageBlockInput,
  type StorageKind,
} from '@wms/domain';
import type { ReactNode } from 'react';
import { fmt } from '../../../format';
import { Icon } from '../../../ui/icons';

// Forms of the warehouse editor: a block of rows, a line of floor areas, a selected row.

/** "1,2" or "1.2" or "-3" as typed → metres; NaN when it is not a number. */
export function metres(text: string): number {
  const v = text.trim().replace(/\s/g, '').replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : NaN;
}
export const metreText = (v: number) => String(Math.round(v * 1000) / 1000).replace('.', ',');

export function Stepper(props: { label: string; value: number; min: number; max: number; onChange: (v: number) => void; hint?: string }) {
  const { value, min, max, onChange } = props;
  const set = (v: number) => onChange(Math.min(max, Math.max(min, Math.round(v))));
  return (
    <div className="field">
      <label>{props.label}</label>
      <div className="stepper">
        <button type="button" className="btn sm" onClick={() => set(value - 1)} disabled={value <= min} aria-label={`${props.label}: azalt`}>
          −
        </button>
        <input className="input" inputMode="numeric" value={value} onChange={(e) => set(Number(e.target.value.replace(/\D/g, '')) || min)} aria-label={props.label} />
        <button type="button" className="btn sm" onClick={() => set(value + 1)} disabled={value >= max} aria-label={`${props.label}: artır`}>
          +
        </button>
      </div>
      {props.hint && <span className="hint">{props.hint}</span>}
    </div>
  );
}

export function MetreField(props: { id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; allowNegative?: boolean }) {
  const v = metres(props.value);
  const bad = !Number.isFinite(v) || (!props.allowNegative && v < 0);
  return (
    <div className="field">
      <label htmlFor={props.id}>{props.label}</label>
      <div className="unit-input">
        <input id={props.id} className={`input${bad ? ' invalid' : ''}`} inputMode="decimal" value={props.value} onChange={(e) => props.onChange(e.target.value)} />
        <span>m</span>
      </div>
      {props.hint && <span className="hint">{props.hint}</span>}
    </div>
  );
}

/** A block of rows as typed: sizes stay text until they are read. */
export interface RowForm {
  readonly kind: StorageKind;
  readonly rows: number;
  /** the first letter; '' = the next free one */
  readonly start: string;
  readonly racks: number;
  readonly levels: number;
  readonly bins: number;
  readonly width: string;
  readonly depth: string;
  readonly levelHeight: string;
  readonly gap: string;
  readonly walkway: string;
  readonly backGap: string;
  readonly arrangement: 'paired' | 'single';
  readonly flip: boolean;
  readonly type: 'pick' | 'bulk';
  /** '' = worked out from the sizes */
  readonly litres: string;
  readonly kg: string;
  readonly zone: string;
  readonly x: string;
  readonly y: string;
}

export function rowFormFor(kind: StorageKind, base?: Partial<RowForm>): RowForm {
  const p = RACK_PRESETS[kind];
  return {
    rows: 2,
    start: '',
    racks: kind === 'shelf' ? 8 : 6,
    x: '1',
    y: '1',
    zone: '',
    backGap: '0,1',
    arrangement: 'paired',
    flip: false,
    ...base,
    kind,
    levels: p.levels,
    bins: p.bins,
    width: metreText(p.width),
    depth: metreText(p.depth),
    levelHeight: metreText(p.levelHeight),
    gap: metreText(p.gap),
    walkway: metreText(p.walkway),
    type: p.type,
    litres: '',
    kg: String(p.maxWeightKg),
  };
}

export function rowBlock(f: RowForm, aisles: readonly string[], axis: 'x' | 'y'): StorageBlockInput {
  const width = metres(f.width);
  const depth = metres(f.depth);
  const levelHeight = metres(f.levelHeight);
  const litres = f.litres.trim() ? metres(f.litres) : suggestedBinLitres(width, depth, levelHeight, f.bins);
  const kg = f.kg.trim() ? metres(f.kg) : null;
  return {
    kind: f.kind,
    type: f.type,
    aisles,
    racks: { from: 1, to: f.racks },
    levels: { from: 1, to: f.levels },
    bins: { from: 1, to: f.bins },
    width,
    depth,
    levelHeight,
    gap: metres(f.gap),
    walkway: metres(f.walkway),
    backGap: metres(f.backGap),
    arrangement: f.arrangement,
    flip: f.flip,
    origin: { x: metres(f.x), y: metres(f.y) },
    aisleAxis: axis,
    maxVolumeCm3: litres === null || !Number.isFinite(litres) ? null : Math.max(1, Math.round(litres * 1000)),
    maxWeightG: kg === null || !Number.isFinite(kg) ? null : Math.max(1, Math.round(kg * 1000)),
    zone: f.zone.trim() || null,
  };
}

const GAP_LABELS: Readonly<Record<StorageKind, string>> = {
  shelf: 'Raflar arası boşluk',
  pallet_rack: 'Raflar arası boşluk',
  floor: 'Paletler arası boşluk',
  ibc: 'Tanklar arası boşluk',
};

/** A letter the first new row can take, or what uses it. */
export interface LetterChoice {
  readonly aisle: string;
  readonly use: AisleUse | null;
}

const switchedOff = (u: AisleUse) => u.kind !== 'area' && u.bins > 0 && u.inactive === u.bins;

/** A to Z (and on, past the last row), each free or with what uses it. */
export function letterChoices(uses: readonly AisleUse[], rows: number): LetterChoice[] {
  const byAisle = new Map(uses.map((u) => [u.aisle, u]));
  const top = Math.min(702, Math.max(26, ...uses.filter((u) => u.kind !== 'area').map((u) => aisleIndex(u.aisle) + rows + 1)));
  return Array.from({ length: top }, (_, i) => {
    const aisle = aisleAt(i + 1);
    return { aisle, use: byAisle.get(aisle) ?? null };
  });
}

/**
 * Why the new rows do not start at the first letters: areas built with row letters or rows
 * switched off use them, or a free gap is too short for the rows of the block side by side.
 */
export function skippedNote(uses: readonly AisleUse[], letters: readonly string[], rows: number): string | null {
  const first = letters[0] ? aisleIndex(letters[0]) : 0;
  const byAisle = new Map(uses.map((u) => [u.aisle, u]));
  const skipped: string[] = [];
  for (let n = 1; n < first; n++) {
    const aisle = aisleAt(n);
    const use = byAisle.get(aisle);
    if (!use) skipped.push(`${aisle} (boş, ama ${rows} sıra art arda sığmıyor)`);
    else if (use.kind === 'area') skipped.push(`${aisle} (${lowerLabel(use.label)})`);
    else if (switchedOff(use)) skipped.push(`${aisle} (pasif sıra)`);
  }
  return skipped.length ? `Atlanan harfler: ${skipped.join(', ')}.` : null;
}

export function RowFormView(props: { form: RowForm; onChange: (f: RowForm) => void; letters: readonly string[]; options: readonly LetterChoice[]; note: string | null }) {
  const { form: f, onChange } = props;
  const set = <K extends keyof RowForm>(key: K, value: RowForm[K]) => onChange({ ...f, [key]: value });
  const preset = RACK_PRESETS[f.kind];
  const auto = suggestedBinLitres(metres(f.width), metres(f.depth), metres(f.levelHeight), f.bins);
  return (
    <div className="stack">
      <div className="field">
        <label>Ne ekliyorsunuz?</label>
        <div className="kind-tiles">
          {STORAGE_KINDS.map((k) => (
            <button key={k} type="button" className={`kind-tile${f.kind === k ? ' on' : ''}`} aria-pressed={f.kind === k} onClick={() => onChange(rowFormFor(k, f))}>
              <KindIcon kind={k} />
              <b>{RACK_PRESETS[k].label}</b>
              <small>{RACK_PRESETS[k].hint}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="form-grid">
        <Stepper label="Kaç sıra" value={f.rows} min={1} max={26} onChange={(v) => set('rows', v)} />
        <Stepper label={`Her sırada kaç ${preset.unit}`} value={f.racks} min={1} max={99} onChange={(v) => set('racks', v)} />
        <Stepper label="Kat sayısı" value={f.levels} min={1} max={20} onChange={(v) => set('levels', v)} hint={f.kind === 'floor' || f.kind === 'ibc' ? 'Üst üste kaç tane konur' : undefined} />
        <Stepper label="Her katta göz" value={f.bins} min={1} max={20} onChange={(v) => set('bins', v)} hint={f.kind === 'pallet_rack' ? 'Bir katta yan yana kaç palet' : undefined} />
      </div>
      <div className="letters">
        <span className="muted">Sıra harfleri:</span>
        {props.letters.map((l) => (
          <span key={l} className="chip info mono">
            {l}
          </span>
        ))}
        <label className="muted" htmlFor="row-start">
          ilk harf
        </label>
        <select id="row-start" className="select sm" value={f.start} onChange={(e) => set('start', e.target.value)}>
          <option value="">Otomatik</option>
          {props.options.map(({ aisle, use }) => (
            <option key={aisle} value={aisle} disabled={use !== null}>
              {use ? `${aisle} · ${lowerLabel(use.label)}${switchedOff(use) ? ' (pasif)' : ''}` : aisle}
            </option>
          ))}
        </select>
      </div>
      {props.note && <span className="hint">{props.note}</span>}

      <h3>Ölçüler</h3>
      <div className="form-grid">
        <MetreField id="r-width" label={`${preset.label} genişliği (sıra boyunca)`} value={f.width} onChange={(v) => set('width', v)} />
        <MetreField id="r-depth" label="Derinlik" value={f.depth} onChange={(v) => set('depth', v)} />
        <MetreField id="r-level" label="Kat yüksekliği" value={f.levelHeight} onChange={(v) => set('levelHeight', v)} />
        <MetreField id="r-gap" label={GAP_LABELS[f.kind]} value={f.gap} onChange={(v) => set('gap', v)} hint="Yan yana iki yer arasındaki boşluk; bitişikse 0" />
        <MetreField id="r-walk" label="Koridor genişliği" value={f.walkway} onChange={(v) => set('walkway', v)} hint="Sıranın önündeki yürüme yolu" />
      </div>

      <div className="field">
        <label>Sıraların dizilişi</label>
        <div className="choice-list">
          <label className={`choice${f.arrangement === 'paired' ? ' on' : ''}`}>
            <input type="radio" name="arrangement" checked={f.arrangement === 'paired'} onChange={() => set('arrangement', 'paired')} />
            <span>
              <b>Karşılıklı</b>
              <small>İki sıra bir koridoru paylaşır (A ile B yüz yüze, B ile C sırt sırta)</small>
            </span>
          </label>
          <label className={`choice${f.arrangement === 'single' ? ' on' : ''}`}>
            <input type="radio" name="arrangement" checked={f.arrangement === 'single'} onChange={() => set('arrangement', 'single')} />
            <span>
              <b>Tek yönlü</b>
              <small>Her sıranın önünde kendi koridoru var (duvar dibi raflar)</small>
            </span>
          </label>
        </div>
        <div className="row">
          <button type="button" className={`btn sm${f.flip ? ' on' : ''}`} onClick={() => set('flip', !f.flip)} aria-pressed={f.flip}>
            <Icon name="flip" size={16} /> Yönü çevir
          </button>
          {f.arrangement === 'paired' && f.rows > 2 && <MetreField id="r-back" label="Sırt sırta sıralar arası" value={f.backGap} onChange={(v) => set('backGap', v)} />}
        </div>
      </div>

      <h3>Konum</h3>
      <div className="form-grid">
        <MetreField id="r-x" label="Bloğun köşesi X" value={f.x} onChange={(v) => set('x', v)} allowNegative />
        <MetreField id="r-y" label="Bloğun köşesi Y" value={f.y} onChange={(v) => set('y', v)} allowNegative />
      </div>
      <span className="hint">Mavi bloğu haritada sürükleyerek de yerleştirebilirsiniz. Köşe: bloğun üstten görünümdeki sol alt köşesi; Ölçek düğmesi metreleri gösterir.</span>

      <h3>Gözler</h3>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="r-type">Kullanım</label>
          <select id="r-type" className="select" value={f.type} onChange={(e) => set('type', e.target.value as 'pick' | 'bulk')}>
            <option value="pick">Toplama rafı (siparişler buradan toplanır)</option>
            <option value="bulk">Yedek alan (toplu stok, palet)</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="r-litres">Göz hacmi</label>
          <div className="unit-input">
            <input id="r-litres" className="input" inputMode="decimal" value={f.litres} placeholder={auto ? fmt.n(auto) : ''} onChange={(e) => set('litres', e.target.value)} />
            <span>litre</span>
          </div>
          <span className="hint">{f.litres.trim() ? 'Elle girildi' : 'Ölçülerden hesaplandı; değiştirebilirsiniz'}</span>
        </div>
        <div className="field">
          <label htmlFor="r-kg">Göz taşıma kapasitesi</label>
          <div className="unit-input">
            <input id="r-kg" className="input" inputMode="decimal" value={f.kg} onChange={(e) => set('kg', e.target.value)} />
            <span>kg</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="r-zone">Bölge (isteğe bağlı)</label>
          <input id="r-zone" className="input" value={f.zone} onChange={(e) => set('zone', e.target.value)} placeholder="Soğuk oda, Z1…" />
        </div>
      </div>
    </div>
  );
}

/** A line of floor areas as typed. */
export interface AreaForm {
  readonly type: AreaType;
  readonly count: number;
  readonly width: string;
  readonly depth: string;
  readonly gap: string;
  readonly x: string;
  readonly y: string;
}

export const areaFormFor = (type: AreaType, base?: Partial<AreaForm>): AreaForm => ({
  count: 1,
  gap: '0,5',
  x: '0',
  y: '0',
  ...base,
  type,
  width: metreText(AREA_PRESETS[type].width),
  depth: metreText(AREA_PRESETS[type].depth),
});

export function areaBlock(f: AreaForm, first: number, axis: 'x' | 'y'): AreaBlockInput {
  return {
    kind: 'area',
    type: f.type,
    aisle: AREA_PRESETS[f.type].code,
    racks: { from: first, to: Math.min(99, first + f.count - 1) },
    width: metres(f.width),
    depth: metres(f.depth),
    gap: metres(f.gap),
    origin: { x: metres(f.x), y: metres(f.y) },
    aisleAxis: axis,
  };
}

export function AreaFormView(props: { form: AreaForm; onChange: (f: AreaForm) => void; codes: readonly string[] }) {
  const { form: f, onChange } = props;
  const set = <K extends keyof AreaForm>(key: K, value: AreaForm[K]) => onChange({ ...f, [key]: value });
  const preset = AREA_PRESETS[f.type];
  return (
    <div className="stack">
      <div className="field">
        <label>Hangi alan?</label>
        <div className="kind-tiles areas">
          {AREA_TYPES.map((t) => (
            <button key={t} type="button" className={`kind-tile${f.type === t ? ' on' : ''}`} aria-pressed={f.type === t} onClick={() => onChange(areaFormFor(t, f))}>
              <b>{AREA_PRESETS[t].label}</b>
              <small className="mono">{AREA_PRESETS[t].code}-01</small>
            </button>
          ))}
        </div>
        <span className="hint">{preset.hint}</span>
      </div>
      <div className="form-grid">
        <Stepper label="Kaç tane" value={f.count} min={1} max={20} onChange={(v) => set('count', v)} />
        <MetreField id="a-width" label="Genişlik" value={f.width} onChange={(v) => set('width', v)} />
        <MetreField id="a-depth" label="Derinlik" value={f.depth} onChange={(v) => set('depth', v)} />
        {f.count > 1 && <MetreField id="a-gap" label="Aralarındaki boşluk" value={f.gap} onChange={(v) => set('gap', v)} />}
      </div>
      <div className="letters">
        <span className="muted">Numaraları:</span>
        {props.codes.map((c) => (
          <span key={c} className="chip info mono">
            {c}
          </span>
        ))}
      </div>
      <div className="form-grid">
        <MetreField id="a-x" label="Köşesi X" value={f.x} onChange={(v) => set('x', v)} allowNegative />
        <MetreField id="a-y" label="Köşesi Y" value={f.y} onChange={(v) => set('y', v)} allowNegative />
      </div>
      <span className="hint">Mavi alanı haritada sürükleyerek de yerleştirebilirsiniz.</span>
    </div>
  );
}

/** A building on the floor plan (the office) as typed: its corner like a block's, sizes as text. */
export interface BuildingForm {
  readonly name: string;
  readonly width: string;
  readonly depth: string;
  readonly height: string;
  readonly x: string;
  readonly y: string;
  /** a new building: put the route start (P) at its door */
  readonly startHere: boolean;
}

const round1 = (v: number) => Math.round(v * 1000) / 1000;

export const buildingFormOf = (b: Pick<FloorBuilding, 'name' | 'x' | 'y' | 'width' | 'depth' | 'height'>, startHere = false): BuildingForm => ({
  name: b.name,
  width: metreText(b.width),
  depth: metreText(b.depth),
  height: metreText(b.height),
  x: metreText(round1(b.x - b.width / 2)),
  y: metreText(round1(b.y - b.depth / 2)),
  startHere,
});

/** The building the form describes, or null while a number is half typed. */
export function buildingOf(id: string, f: BuildingForm): FloorBuilding | null {
  const [width, depth, height, x, y] = [f.width, f.depth, f.height, f.x, f.y].map(metres) as [number, number, number, number, number];
  if (![width, depth, height, x, y].every(Number.isFinite) || width <= 0 || depth <= 0 || height <= 0 || !f.name.trim()) return null;
  return { id, name: f.name.trim(), x: round1(x + width / 2), y: round1(y + depth / 2), width, depth, height };
}

const BUILDING_NAMES = ['Ofis', 'Yemekhane', 'Soyunma odası', 'WC', 'Toplantı odası', 'Teknik oda'];

export function BuildingFormView(props: { form: BuildingForm; onChange: (f: BuildingForm) => void; adding: boolean }) {
  const { form: f, onChange } = props;
  const set = <K extends keyof BuildingForm>(key: K, value: BuildingForm[K]) => onChange({ ...f, [key]: value });
  return (
    <div className="stack">
      <div className="form-grid">
        <div className="field">
          <label htmlFor="bd-name">Adı</label>
          <input id="bd-name" className="input" list="bd-names" maxLength={40} value={f.name} onChange={(e) => set('name', e.target.value)} />
          <datalist id="bd-names">
            {BUILDING_NAMES.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </div>
        <MetreField id="bd-w" label="Genişlik (X yönünde)" value={f.width} onChange={(v) => set('width', v)} />
        <MetreField id="bd-d" label="Derinlik (Y yönünde)" value={f.depth} onChange={(v) => set('depth', v)} />
        <MetreField id="bd-h" label="Yükseklik" value={f.height} onChange={(v) => set('height', v)} hint="Tek katlı ofis için 3 m" />
        <MetreField id="bd-x" label="Köşesi X" value={f.x} onChange={(v) => set('x', v)} allowNegative />
        <MetreField id="bd-y" label="Köşesi Y" value={f.y} onChange={(v) => set('y', v)} allowNegative />
      </div>
      <span className="hint">Binayı haritada sürükleyerek de yerleştirebilirsiniz. Bina stok tutmaz; haritada yer gösterir.</span>
      {props.adding && (
        <label className={`choice${f.startHere ? ' on' : ''}`}>
          <input type="checkbox" checked={f.startHere} onChange={(e) => set('startHere', e.target.checked)} />
          <span>
            <b>Toplama rotaları buradan başlasın</b>
            <small>Rota başlangıcı (P), binanın raflara bakan kapısına konur</small>
          </span>
        </label>
      )}
    </div>
  );
}

/** Sizes of a selected row (or a single position) as typed. */
export interface SizeForm {
  readonly kind: RackKind;
  readonly width: string;
  readonly depth: string;
  readonly levelHeight: string;
  readonly gap: string;
  readonly walkway: string;
}

export const sizeFormOf = (r: RackGeometry, gap: number): SizeForm => ({
  kind: r.kind,
  width: metreText(r.width),
  depth: metreText(r.depth),
  levelHeight: metreText(r.levelHeight),
  gap: metreText(gap),
  walkway: metreText(r.reach * 2),
});

export function SizeFormView(props: { form: SizeForm; onChange: (f: SizeForm) => void; area: boolean; single: boolean }) {
  const { form: f, onChange } = props;
  const set = <K extends keyof SizeForm>(key: K, value: SizeForm[K]) => onChange({ ...f, [key]: value });
  return (
    <div className="form-grid">
      {!props.area && (
        <div className="field">
          <label htmlFor="s-kind">Türü</label>
          <select id="s-kind" className="select" value={f.kind} onChange={(e) => set('kind', e.target.value as RackKind)}>
            {STORAGE_KINDS.map((k) => (
              <option key={k} value={k}>
                {RACK_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
      )}
      <MetreField id="s-width" label="Genişlik" value={f.width} onChange={(v) => set('width', v)} />
      <MetreField id="s-depth" label="Derinlik" value={f.depth} onChange={(v) => set('depth', v)} />
      {!props.area && <MetreField id="s-level" label="Kat yüksekliği" value={f.levelHeight} onChange={(v) => set('levelHeight', v)} />}
      {!props.area && !props.single && <MetreField id="s-gap" label="Aradaki boşluk" value={f.gap} onChange={(v) => set('gap', v)} />}
      {!props.area && <MetreField id="s-walk" label="Önündeki koridor" value={f.walkway} onChange={(v) => set('walkway', v)} />}
    </div>
  );
}

/** Small drawings of the rack kinds for the tiles. */
function KindIcon({ kind }: { kind: StorageKind }) {
  const paths: Record<StorageKind, ReactNode> = {
    shelf: (
      <>
        <path d="M6 4v24M26 4v24M6 10h20M6 17h20M6 24h20" />
        <path d="M9 7h4v3H9zM15 14h5v3h-5zM10 21h6v3h-6z" className="fill" />
      </>
    ),
    pallet_rack: (
      <>
        <path d="M4 4v24M28 4v24M4 15h24M4 27h24" />
        <path d="M7 9h8v6H7zM17 9h8v6h-8zM7 21h8v6H7z" className="fill" />
      </>
    ),
    floor: (
      <>
        <path d="M5 26h22M5 29h22M8 26v3M16 26v3M24 26v3" />
        <path d="M7 12h18v14H7z" className="fill" />
      </>
    ),
    ibc: (
      <>
        <path d="M6 6h20v20H6zM6 13h20M6 20h20M13 6v20M19 6v20M5 28h22" />
        <path d="M14 3h4v3h-4z" className="fill" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 32 32" width={34} height={34} className="kind-icon" aria-hidden>
      {paths[kind]}
    </svg>
  );
}

/** The step-by-step guide shown on top of the editor. */
export function EditorHelp({ open }: { open: boolean }) {
  return (
    <details className="editor-help" open={open}>
      <summary>Depo tanımı nasıl yapılır?</summary>
      <ol>
        <li>
          <b>Raf sırası ekleyin.</b> Türünü seçin (raf, palet rafı, yer paleti, IBC tank), kaç sıra ve her sırada kaç yer olduğunu yazın. Sıra harfleri
          kendiliğinden verilir (A, B, C…); isterseniz ilk harfi seçebilirsiniz.
        </li>
        <li>
          <b>Ölçüleri girin.</b> Genişlik, derinlik, kat yüksekliği, yan yana yerler arasındaki boşluk ve koridor genişliği. Harita gerçek ölçüyle çizilir;
          <b> Ölçek</b> düğmesi metre ızgarasını açar.
        </li>
        <li>
          <b>Yerine koyun.</b> Mavi bloğu haritada sürükleyin (ya da köşesinin X ve Y değerini yazın), sonra <b>Ekle</b>’ye basın. Eklenen raflar haritada
          yanıp söner ve altta listelenir.
        </li>
        <li>
          <b>Alanları ekleyin:</b> mal kabul, paketleme masaları, sevk alanı, iade, hasarlı ve karantina. Her biri numara alır (PK-01, SV-01…). Paketleme
          masaları aynı kodla paketleme istasyonu olarak da açılır.
        </li>
        <li>
          <b>Düzeltin.</b> Bir rafa dokunun: sırayı sürükleyerek taşıyın, yönünü çevirin, ölçüsünü değiştirin ya da yanlış eklediyseniz silin. Değişiklik
          <b> Kaydet</b>’e basınca işlenir.
        </li>
        <li>
          <b>Bina ekleyin</b> (ofis, yemekhane): gerçek ölçüsüyle haritada görünür, stok tutmaz.
        </li>
        <li>
          <b>P işaretini</b> (toplama rotalarının başladığı yer) sürükleyin; ofisten başlanacaksa binaya dokunup <b>Rota başlangıcı burası</b>’na basın.
        </li>
        <li>
          <b>Etiket bas</b> sekmesinden göz etiketlerini basıp raflara yapıştırın.
        </li>
      </ol>
      <p className="muted">
        Göz kodu <span className="mono">A-03-2-1</span>: A sırası, 3. raf, 2. kat, 1. göz. Katlar yerden yukarı, gözler soldan sağa sayılır.
      </p>
    </details>
  );
}
